import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Zap, Loader2, CheckCircle2, TrendingUp, Check, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { GOLD, DARK, CARD, MODAL_BG, MODAL_PANEL } from "./constants";

// ── Bid-specific constants ───────────────────────────────────────────────────
const EQUIPMENT_PACKAGES = {
  semi_truck: ["Dry Van", "Reefer", "Flatbed", "Step Deck", "Tanker", "Lowboy", "Power Only"],
  box_truck:  ["26ft Box", "20ft Box", "Sprinter/Cargo Van", "Hotshot"],
};
const PAY_PROGRAMS_UI = {
  company_driver: { label: "Company Driver", payType: "cpm",        min: 0.70, placeholder: "0.75", unit: "¢/mi CPM" },
  rental:         { label: "Rental",         payType: "percentage", min: 85,   placeholder: "85",   unit: "% per load" },
  lease:          { label: "Lease Purchase", payType: "percentage", min: 85,   placeholder: "85",   unit: "% per load" },
  owner_operator: { label: "Owner Operator", payType: "percentage", min: 88,   placeholder: "88",   unit: "% gross" },
};
const EQUIPMENT_CPM_MIN_UI = {
  "Dry Van": 0.70, "Reefer": 0.80, "Flatbed": 0.95, "Step Deck": 0.90,
  "Tanker": 0.85, "Lowboy": 0.90, "Power Only": 0.70,
  "26ft Box": 0.55, "20ft Box": 0.55, "Sprinter/Cargo Van": 0.55,
  "Hotshot": 0.55, "Rental Box": 0.55,
};
const PROGRAM_MIN_FEE_UI = {
  semi_truck: { company_driver: 1500, rental: 1700, lease: 1900, owner_operator: 2100 },
  box_truck:  { company_driver:  800, rental: 1000, lease: 1200, owner_operator: 1400 },
};
const PROGRAM_DISCOUNT_UI = {
  semi_truck: { company_driver: 300, rental: 400, lease: 500, owner_operator: 600 },
  box_truck:  { company_driver: 200, rental: 250, lease: 300, owner_operator: 350 },
};
const BID_ACCENT = "#f59e0b";

function getFlexSubType(position_type, pay_program, weeks) {
  const w = `_${weeks}w`;
  if (position_type === "semi_truck") {
    if (pay_program === "company_driver") return `semi_company_driver${w}`;
    if (pay_program === "rental")         return `semi_rental${w}`;
    if (pay_program === "lease")          return `semi_lease${w}`;
    if (pay_program === "owner_operator") return `owner_operator_semi${w}`;
  }
  if (position_type === "box_truck") {
    if (pay_program === "company_driver") return `box_truck_company_driver${w}`;
    if (pay_program === "rental")         return `box_truck_rental${w}`;
    if (pay_program === "lease")          return `box_truck_lease${w}`;
    if (pay_program === "owner_operator") return `box_truck_owner${w}`;
  }
  return `semi_company_driver${w}`;
}
const WEEKLY_FLEX_PREVIEW = {
  semi_truck: {
    company_driver: [{ weeks:9,price:275 },{weeks:17,price:185,popular:true},{weeks:35,price:120},{weeks:52,price:95,daily:"~$14/day"}],
    rental:         [{ weeks:9,price:310 },{weeks:17,price:205,popular:true},{weeks:35,price:135},{weeks:52,price:110,daily:"~$16/day"}],
    lease:          [{ weeks:9,price:340 },{weeks:17,price:230,popular:true},{weeks:35,price:150},{weeks:52,price:125,daily:"~$18/day"}],
    owner_operator: [{ weeks:9,price:375 },{weeks:17,price:255,popular:true},{weeks:35,price:165},{weeks:52,price:135,daily:"~$19/day"}],
  },
  box_truck: {
    company_driver: [{ weeks:9,price:150 },{weeks:17,price:100,popular:true},{weeks:35,price:65 },{weeks:52,price:55,daily:"~$8/day"  }],
    rental:         [{ weeks:9,price:180 },{weeks:17,price:125,popular:true},{weeks:35,price:80 },{weeks:52,price:65,daily:"~$9/day"  }],
    lease:          [{ weeks:9,price:215 },{weeks:17,price:150,popular:true},{weeks:35,price:95 },{weeks:52,price:80,daily:"~$11/day" }],
    owner_operator: [{ weeks:9,price:250 },{weeks:17,price:170,popular:true},{weeks:35,price:110},{weeks:52,price:90,daily:"~$13/day" }],
  },
};

// ── BidModal ─────────────────────────────────────────────────────────────────
export function BidModal({ driver, token, onClose, onSuccess, cashbackBalance = 0, onAgreementRequired }) {
  const [form, setForm] = useState({
    position_type:          "semi_truck",
    equipment_package:      "Dry Van",
    pay_program:            "company_driver",
    driver_pay_cpm:         String(EQUIPMENT_CPM_MIN_UI["Dry Van"]),
    driver_pay_percentage:  "",
    driver_pay_type:        "cpm",   // "cpm" | "ppl" — only applies when pay_program === "company_driver"
    driver_pay_package:     "",
    placement_fee:          String(PROGRAM_MIN_FEE_UI.semi_truck.company_driver),
    payment_option:         "standard",
    offer_truck_model:      "",
    offer_truck_year_from:  "",
    offer_truck_year_to:    "",
    offer_weeks_out:        "",
    offer_days_off:         "",
    offer_miles_from:       "",
    offer_miles_to:         "",
    offer_signup_bonus:     "",
    offer_safety_bonus:     "",
    offer_dotw_bonus:       "",
    offer_dotm_bonus:       "",
  });
  const [applyCashback, setApplyCashback] = useState(false);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState("");
  const [flexEmail, setFlexEmail]         = useState("");
  const [flexDuration, setFlexDuration]   = useState(17);
  const [flexSubscribed, setFlexSubscribed]   = useState(false);
  const [flexSubscribing, setFlexSubscribing] = useState(false);
  const [shareCopied, setShareCopied]     = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Auto-fill minimums when position type, pay program, equipment, or payment option changes
  const { position_type, pay_program, equipment_package, payment_option } = form;
  useEffect(() => {
    const newBaseFee = PROGRAM_MIN_FEE_UI[position_type]?.[pay_program] ?? 1500;
    const discount   = payment_option === "one_time_discount"
      ? (PROGRAM_DISCOUNT_UI[position_type]?.[pay_program] ?? 300)
      : 0;
    const newFee  = Math.max(0, newBaseFee - discount);
    const newCpm  = EQUIPMENT_CPM_MIN_UI[equipment_package] ?? 0.70;
    const newPct  = PAY_PROGRAMS_UI[pay_program]?.min ?? 85;
    const isCompanyDriver = pay_program === "company_driver";
    setForm((f) => ({
      ...f,
      // Reset pay type to CPM when switching away from company_driver
      driver_pay_type: isCompanyDriver ? f.driver_pay_type : "cpm",
      placement_fee:         String(newFee),
      driver_pay_cpm:        isCompanyDriver ? String(newCpm) : f.driver_pay_cpm,
      driver_pay_percentage: !isCompanyDriver ? String(newPct) : f.driver_pay_percentage,
    }));
  }, [position_type, pay_program, equipment_package, payment_option]); // eslint-disable-line

  const isCpmProgram  = form.pay_program === "company_driver" && form.driver_pay_type === "cpm";
  const isPplProgram  = form.pay_program === "company_driver" && form.driver_pay_type === "ppl";
  const PPL_MIN       = 25;
  const equipCpmMin   = EQUIPMENT_CPM_MIN_UI[form.equipment_package] ?? 0.70;
  const minPay        = isCpmProgram ? equipCpmMin
                       : isPplProgram ? PPL_MIN
                       : (PAY_PROGRAMS_UI[form.pay_program]?.min ?? 85);
  const baseFee       = PROGRAM_MIN_FEE_UI[form.position_type]?.[form.pay_program] ?? 1500;
  const topBid        = driver?.highest_bid ?? 0;
  const minFee        = baseFee;   // always allow floor price regardless of existing bids
  const oneTimeDiscount = PROGRAM_DISCOUNT_UI[form.position_type]?.[form.pay_program] ?? 300;
  const isOneTime       = form.payment_option === "one_time_discount";
  // When One-Time Payment is selected, carrier can bid the net amount (floor minus discount)
  const effectiveMinFee = isOneTime ? Math.max(0, minFee - oneTimeDiscount) : minFee;

  const parseIntFee = (val) => parseInt(val, 10);

  async function handleFlexSubscribe() {
    if (!flexEmail) { toast.error("Enter your email address first"); return; }
    setFlexSubscribing(true);
    try {
      const selected = flexPreview.find((o) => o.weeks === flexDuration) || flexPreview[1];
      await api.marketWeeklyFlexSubscribe(token, {
        email: flexEmail,
        position_type: form.position_type,
        pay_program: form.pay_program,
        duration_weeks: flexDuration,
        weekly_price: selected.price,
      });
      setFlexSubscribed(true);
      toast.success("Interest registered — Hylas will contact you to set up billing.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setFlexSubscribing(false); }
  }

  async function submit(e) {
    e.preventDefault();
    setError("");
    const isWeeklyFlex = form.payment_option === "weekly_flex";
    const fee = isWeeklyFlex ? 0 : parseIntFee(form.placement_fee);
    if (!isWeeklyFlex && (isNaN(fee) || fee < effectiveMinFee)) {
      setError(`Minimum placement fee for ${PAY_PROGRAMS_UI[form.pay_program]?.label}: $${effectiveMinFee.toLocaleString()}`);
      return;
    }
    if (isCpmProgram) {
      if (!form.driver_pay_cpm || parseFloat(form.driver_pay_cpm) < minPay) {
        setError(`Min CPM for ${form.equipment_package}: $${minPay.toFixed(2)}/mi`); return;
      }
    } else if (isPplProgram) {
      if (!form.driver_pay_percentage || parseFloat(form.driver_pay_percentage) < PPL_MIN) {
        setError(`Min PPL for Company Driver: ${PPL_MIN}%`); return;
      }
    } else {
      if (!form.driver_pay_percentage || parseFloat(form.driver_pay_percentage) < minPay) {
        setError(`Min % for ${PAY_PROGRAMS_UI[form.pay_program]?.label}: ${minPay}%`); return;
      }
    }
    setLoading(true);
    try {
      const parts = [];
      if (form.offer_truck_model) {
        const yrs = [form.offer_truck_year_from, form.offer_truck_year_to].filter(Boolean);
        parts.push(`Truck: ${form.offer_truck_model}${yrs.length ? ` (${yrs.join("–")})` : ""}`);
      }
      if (form.offer_weeks_out || form.offer_days_off) {
        const htParts = [];
        if (form.offer_weeks_out) htParts.push(`${form.offer_weeks_out} wk${form.offer_weeks_out !== "1" ? "s" : ""} out`);
        if (form.offer_days_off)  htParts.push(`${form.offer_days_off} days off`);
        parts.push(`Home Time: ${htParts.join(" / ")}`);
      }
      if (form.offer_miles_from || form.offer_miles_to) {
        const mParts = [form.offer_miles_from, form.offer_miles_to].filter(Boolean);
        parts.push(`Miles/week: ${mParts.map(m => Number(m).toLocaleString()).join("–")}`);
      }
      if (form.offer_signup_bonus && form.offer_signup_bonus !== "0")  parts.push(`Signup Bonus: $${Number(form.offer_signup_bonus).toLocaleString()}`);
      if (form.offer_safety_bonus && form.offer_safety_bonus !== "0")  parts.push(`Safety Bonus: $${Number(form.offer_safety_bonus).toLocaleString()}`);
      if (form.offer_dotw_bonus   && form.offer_dotw_bonus   !== "0")  parts.push(`Driver of the Week: $${Number(form.offer_dotw_bonus).toLocaleString()}`);
      if (form.offer_dotm_bonus   && form.offer_dotm_bonus   !== "0")  parts.push(`Driver of the Month: $${Number(form.offer_dotm_bonus).toLocaleString()}`);
      const bid_message = parts.length ? parts.join("\n") : null;
      await api.marketPlaceBid(token, {
        driver_id:             driver.id,
        driver_name:           `${driver.first_name} ${driver.last_name}`,
        position_type:         form.position_type,
        equipment_package:     form.equipment_package,
        pay_program:           form.pay_program,
        driver_pay_cpm:         isCpmProgram  ? parseFloat(form.driver_pay_cpm)        : null,
        driver_pay_percentage:  (!isCpmProgram) ? parseFloat(form.driver_pay_percentage) : null,
        driver_pay_package:    form.driver_pay_package || form.equipment_package,
        placement_fee:         fee,
        payment_option:        form.payment_option,
        weekly_flex_sub_type:  isWeeklyFlex ? getFlexSubType(form.position_type, form.pay_program, flexDuration) : null,
        bid_message,
        cashback_applied:      applyCashback ? cashbackBalance : 0,
      });
      toast.success("Bid placed — it is now live on the Market.");
      onSuccess();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail === "AGREEMENT_REQUIRED" && onAgreementRequired) {
        onClose();
        onAgreementRequired();
        return;
      }
      setError(detail || "Failed to place bid.");
    } finally { setLoading(false); }
  }

  const inputCls = "w-full bg-[#111] border border-white/10 text-white px-3 py-2.5 text-sm focus:outline-none focus:border-[#f59e0b] transition-colors";
  const labelCls = "block text-xs font-head uppercase tracking-[0.2em] text-white/50 mb-1.5";

  const PAYMENT_OPTIONS = [
    { key: "standard",          label: "Standard 50/50",    desc: "50% on first day of hire · 50% after 2 weeks · Spread out payments, save upfront." },
    { key: "one_time_discount", label: "One-Time Payment",  desc: "Pay upfront · billed once, no schedule" },
    { key: "weekly_flex",       label: "Hylas Weekly Flex", desc: "Fixed weekly payments · flexible duration" },
  ];

  const flexPreview = WEEKLY_FLEX_PREVIEW[form.position_type]?.[form.pay_program] || WEEKLY_FLEX_PREVIEW.semi_truck.company_driver;

  return (
    <motion.div
      variants={MODAL_BG}
      initial="hidden" animate="show" exit="exit"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.88)", backdropFilter: "blur(10px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        variants={MODAL_PANEL}
        initial="hidden" animate="show" exit="exit"
        className="relative w-full max-w-xl max-h-[90vh] overflow-y-auto"
        style={{ background: CARD, border: `1px solid rgba(245,158,11,0.28)`, boxShadow: `0 0 60px rgba(245,158,11,0.12)` }}
      >
        {/* Amber accent bar */}
        <div style={{ height: 3, background: `linear-gradient(90deg, ${BID_ACCENT}, ${GOLD})` }} />

        {/* Header */}
        <div className="flex items-start justify-between p-5 pb-4">
          <div>
            <p className="font-head text-[10px] uppercase tracking-[0.35em] text-white/35 mb-1">Carrier Bid</p>
            <h2 className="font-head font-black text-xl uppercase tracking-[0.04em]" style={{ color: BID_ACCENT }}>
              {driver.first_name} {driver.last_name}
            </h2>
            <p className="font-head text-xs uppercase tracking-[0.15em] text-white/40 mt-0.5">
              {driver.cdl_class || "CDL-A"} · {driver.state || "—"} · {driver.position || "Driver"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {driver.public_slug && (
              <button
                type="button"
                data-testid="bid-modal-share-btn"
                title="Copy driver share link"
                onClick={() => {
                  const url = `https://hylascompanion.com/driver/${driver.public_slug}`;
                  navigator.clipboard.writeText(url).then(() => {
                    setShareCopied(true);
                    toast.success("Share link copied");
                    setTimeout(() => setShareCopied(false), 2000);
                  });
                }}
                className="flex items-center gap-1.5 px-2.5 py-1.5 font-head text-[9px] uppercase tracking-[0.15em] border transition-all"
                style={{
                  borderColor: shareCopied ? "rgba(74,222,128,0.4)" : "rgba(245,158,11,0.25)",
                  color: shareCopied ? "#4ADE80" : "rgba(245,158,11,0.7)",
                  background: shareCopied ? "rgba(74,222,128,0.06)" : "transparent",
                }}
              >
                {shareCopied ? <Check className="w-3 h-3" /> : <Share2 className="w-3 h-3" />}
                {shareCopied ? "Copied" : "Share"}
              </button>
            )}
            <button onClick={onClose} className="text-white/30 hover:text-white transition-colors p-1">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <form onSubmit={submit} className="px-5 pb-5 space-y-5">
          {/* Vehicle type */}
          <div>
            <label className={labelCls}>Vehicle Type</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(EQUIPMENT_PACKAGES).map(([key]) => (
                <motion.button
                  key={key} type="button" whileTap={{ scale: 0.97 }}
                  onClick={() => setForm((f) => ({ ...f, position_type: key, equipment_package: EQUIPMENT_PACKAGES[key][0] }))}
                  className="py-3 font-head text-sm uppercase tracking-[0.08em] transition-all border"
                  style={{
                    background:  form.position_type === key ? `rgba(245,158,11,0.1)` : "rgba(255,255,255,0.02)",
                    color:       form.position_type === key ? BID_ACCENT : "#9CA3AF",
                    borderColor: form.position_type === key ? BID_ACCENT : "#2a2a2a",
                  }}
                >
                  {key === "semi_truck" ? "Semi Truck" : "Box Truck"}
                  <span className="block text-xs mt-0.5" style={{ color: form.position_type === key ? "rgba(245,158,11,0.65)" : "#6B7280" }}>
                    {key === "semi_truck" ? "$1,500–$2,100+ floor" : "$800–$1,400+ floor"}
                  </span>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Equipment */}
          <div>
            <label className={labelCls}>Equipment Package</label>
            <select value={form.equipment_package} onChange={(e) => setForm((f) => ({ ...f, equipment_package: e.target.value }))} className={inputCls} style={{ background: "#111" }}>
              {(EQUIPMENT_PACKAGES[form.position_type] || []).map((e) => (
                <option key={e} value={e}>{e}</option>
              ))}
            </select>
          </div>

          {/* Pay program */}
          <div>
            <label className={labelCls}>Pay Program</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(PAY_PROGRAMS_UI).map(([key, prog]) => (
                <motion.button
                  key={key} type="button" whileTap={{ scale: 0.97 }}
                  onClick={() => setForm((f) => ({ ...f, pay_program: key }))}
                  className="py-2.5 font-head text-sm uppercase tracking-[0.08em] transition-all border"
                  style={{
                    background:  form.pay_program === key ? `rgba(245,158,11,0.1)` : "rgba(255,255,255,0.02)",
                    color:       form.pay_program === key ? BID_ACCENT : "#9CA3AF",
                    borderColor: form.pay_program === key ? BID_ACCENT : "#2a2a2a",
                  }}
                >
                  {prog.label}
                  <span className="block text-xs mt-0.5" style={{ color: form.pay_program === key ? "rgba(245,158,11,0.6)" : "#6B7280" }}>
                    Min ${(PROGRAM_MIN_FEE_UI[form.position_type]?.[key] || 800).toLocaleString()} · {key === "company_driver" ? (form.position_type === "semi_truck" ? "$0.70 CPM / 25% PPL" : "$0.55 CPM / 25% PPL") : `${prog.min}%`}
                  </span>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Driver pay */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              {/* CPM / PPL toggle — only for Company Driver */}
              {form.pay_program === "company_driver" && (
                <div className="flex gap-1.5 mb-2">
                  {[
                    { key: "cpm", label: "CPM", hint: "Cents / mile" },
                    { key: "ppl", label: "PPL", hint: "% per mile" },
                  ].map(({ key, label, hint }) => (
                    <button
                      key={key}
                      type="button"
                      title={hint}
                      onClick={() => {
                        setForm((f) => ({
                          ...f,
                          driver_pay_type: key,
                          // auto-seed the value when switching
                          driver_pay_cpm:        key === "cpm" ? String(equipCpmMin) : f.driver_pay_cpm,
                          driver_pay_percentage: key === "ppl" ? String(PPL_MIN) : f.driver_pay_percentage,
                        }));
                      }}
                      className="px-2.5 py-1 font-head text-[10px] uppercase tracking-[0.15em] border transition-all"
                      style={{
                        background:  form.driver_pay_type === key ? "rgba(245,158,11,0.12)" : "rgba(255,255,255,0.02)",
                        color:       form.driver_pay_type === key ? BID_ACCENT : "#6B7280",
                        borderColor: form.driver_pay_type === key ? BID_ACCENT : "#2a2a2a",
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {isCpmProgram ? (
                <>
                  <label className={labelCls}>Driver Pay (CPM $)</label>
                  <input data-testid="driver-pay-cpm" type="number" step="0.01" min={equipCpmMin} value={form.driver_pay_cpm} onChange={set("driver_pay_cpm")} placeholder={equipCpmMin.toFixed(2)} className={inputCls} />
                  <p className="text-white/40 text-xs mt-1.5">Min ${equipCpmMin.toFixed(2)}/mi · {form.equipment_package}</p>
                </>
              ) : isPplProgram ? (
                <>
                  <label className={labelCls}>Driver Pay (PPL %)</label>
                  <input data-testid="driver-pay-ppl" type="number" step="0.5" min={PPL_MIN} max="100" value={form.driver_pay_percentage} onChange={set("driver_pay_percentage")} placeholder={`${PPL_MIN}`} className={inputCls} />
                  <p className="text-white/40 text-xs mt-1.5">Min {PPL_MIN}% per mile · Company Driver</p>
                </>
              ) : (
                <>
                  <label className={labelCls}>Driver % {PAY_PROGRAMS_UI[form.pay_program]?.unit}</label>
                  <input data-testid="driver-pay-pct" type="number" step="0.5" min={minPay} max="100" value={form.driver_pay_percentage} onChange={set("driver_pay_percentage")} placeholder={`${minPay}`} className={inputCls} />
                  <p className="text-white/40 text-xs mt-1.5">Min {minPay}%</p>
                </>
              )}
            </div>
            <div>
              <label className={labelCls}>Pay Note (optional)</label>
              <input type="text" value={form.driver_pay_package} onChange={set("driver_pay_package")} placeholder={isCpmProgram ? "e.g. 85¢ + Reefer" : isPplProgram ? "e.g. 30% PPL OTR" : "e.g. 88% OTR"} className={inputCls} />
            </div>
          </div>

          {/* Placement fee */}
          <div>
            <label className={labelCls}>Placement Fee to Hylas ($)</label>
            <input
              type="number"
              value={form.placement_fee}
              onChange={set("placement_fee")}
              placeholder={`${effectiveMinFee}`}
              className={inputCls}
            />
            <p className="text-white/40 text-xs mt-1.5">
              {topBid > 0 ? (
                <span>
                  Floor: <span style={{ color: BID_ACCENT }}>${effectiveMinFee.toLocaleString()}</span>
                  {isOneTime && <span style={{ color: GOLD }}> (-${oneTimeDiscount.toLocaleString()} one-time discount)</span>}
                  {" "}· Current top bid: <span style={{ color: GOLD }}>${topBid.toLocaleString()}</span> · No maximum cap
                </span>
              ) : (
                <span>
                  Floor: <span style={{ color: BID_ACCENT }}>${effectiveMinFee.toLocaleString()}</span>
                  {isOneTime && <span style={{ color: GOLD }}> (-${oneTimeDiscount.toLocaleString()} one-time discount)</span>}
                  {" "}· No maximum cap — bid higher to win
                </span>
              )}
            </p>
            {/* Competing carriers banner */}
            {driver.bid_count > 0 && (
              <div className="mt-3 px-4 py-4" style={{ background: "rgba(245,200,66,0.06)", border: "1px solid rgba(245,200,66,0.25)" }} data-testid="competing-carriers-banner">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 shrink-0" style={{ color: GOLD }} />
                  <p className="font-head text-sm font-black uppercase tracking-[0.08em]" style={{ color: GOLD }}>
                    {driver.bid_count} carrier{driver.bid_count !== 1 ? "s are" : " is"} competing for this driver right now.
                  </p>
                </div>
                <p className="text-xs leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>
                  Increasing your bid boosts your placement, but you're welcome to submit a floor offer and see where you stand.
                </p>
              </div>
            )}
            {/* Cashback redemption toggle */}
            {cashbackBalance > 0 && (
              <div
                className="mt-2 flex items-center gap-3 px-3 py-3 cursor-pointer select-none"
                style={{ background: applyCashback ? "rgba(56,189,248,0.1)" : "rgba(56,189,248,0.04)", border: `1px solid ${applyCashback ? "rgba(56,189,248,0.4)" : "rgba(56,189,248,0.15)"}`, transition: "all 0.2s" }}
                onClick={() => setApplyCashback(v => !v)}
                data-testid="cashback-toggle"
              >
                <div className="w-4 h-4 flex items-center justify-center shrink-0 border" style={{ borderColor: applyCashback ? "#38BDF8" : "rgba(56,189,248,0.3)", background: applyCashback ? "#38BDF8" : "transparent" }}>
                  {applyCashback && <Check className="w-2.5 h-2.5" style={{ color: DARK }} />}
                </div>
                <div className="flex-1">
                  <p className="font-head text-xs font-bold" style={{ color: "#38BDF8" }}>Apply ${cashbackBalance} Cashback</p>
                  <p className="text-[10px] text-white/35 mt-0.5">
                    {applyCashback && form.placement_fee
                      ? `Placement fee after cashback: $${Math.max(0, parseInt(form.placement_fee) - cashbackBalance).toLocaleString()}`
                      : "Apply your safety-earned cashback as a discount on this placement fee"}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Payment structure */}
          <div>
            <label className={labelCls}>Payment Structure</label>
            <div className="space-y-2">
              {PAYMENT_OPTIONS.map((opt) => (
                <motion.button
                  key={opt.key} type="button" whileTap={{ scale: 0.99 }}
                  onClick={() => setForm((f) => ({ ...f, payment_option: opt.key }))}
                  className="w-full flex items-start gap-3 px-4 py-3.5 text-left transition-all border"
                  style={{
                    background:  form.payment_option === opt.key ? "rgba(245,158,11,0.08)" : "rgba(255,255,255,0.02)",
                    borderColor: form.payment_option === opt.key ? BID_ACCENT : "#222",
                    boxShadow:   form.payment_option === opt.key ? `0 0 12px rgba(245,158,11,0.1)` : "none",
                  }}
                >
                  <div className="mt-0.5 w-3.5 h-3.5 rounded-full border flex-shrink-0 flex items-center justify-center"
                    style={{ borderColor: form.payment_option === opt.key ? BID_ACCENT : "#555", background: form.payment_option === opt.key ? BID_ACCENT : "transparent" }} />
                  <div>
                    <p className="font-head text-sm font-bold uppercase tracking-[0.1em]" style={{ color: form.payment_option === opt.key ? BID_ACCENT : "#D1D5DB" }}>
                      {opt.label}
                      {opt.key === "weekly_flex" && (
                        <span className="ml-2 text-[9px] font-black px-1.5 py-0.5 align-middle"
                          style={{ background: "rgba(245,200,66,0.15)", color: GOLD, border: "1px solid rgba(245,200,66,0.3)" }}>Interest</span>
                      )}
                    </p>
                    {opt.key === "weekly_flex" ? (
                      <p className="text-xs mt-0.5 flex items-baseline gap-1 flex-wrap" style={{ color: "#6B7280" }}>
                        Hire a driver for just{" "}
                        <motion.span
                          animate={{ opacity: [1, 0.65, 1], color: ["#F5C842", "#fff", "#F5C842"] }}
                          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                          className="font-head font-black text-sm"
                        >$70 a week!</motion.span>
                      </p>
                    ) : opt.key === "one_time_discount" ? (
                      <p className="text-xs mt-0.5 flex items-baseline gap-1 flex-wrap" style={{ color: "#6B7280" }}>
                        Pay upfront ·{" "}
                        <motion.span
                          animate={{ opacity: [1, 0.65, 1], color: ["#F5C842", "#fff", "#F5C842"] }}
                          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                          className="font-head font-black text-xs"
                        >save ${(PROGRAM_DISCOUNT_UI[form.position_type]?.[form.pay_program] ?? 300).toLocaleString()}</motion.span>
                        {" "}— billed once, no schedule
                      </p>
                    ) : (
                      <p className="text-xs mt-0.5" style={{ color: "#6B7280" }}>{opt.desc}</p>
                    )}
                  </div>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Weekly Flex — Live Interest Form */}
          {form.payment_option === "weekly_flex" && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="space-y-3.5"
              style={{ background: "rgba(245,200,66,0.03)", border: "1px solid rgba(245,200,66,0.18)", padding: "16px" }}>
              <div className="flex items-center gap-2.5 flex-wrap">
                <span className="font-head text-sm font-black uppercase tracking-[0.15em]" style={{ color: GOLD }}>Select Your Plan</span>
                <span className="font-head text-[9px] uppercase tracking-[0.12em] px-2 py-0.5"
                  style={{ background: "rgba(245,200,66,0.08)", color: "rgba(245,200,66,0.7)", border: "1px solid rgba(245,200,66,0.2)" }}>
                  Hylas sets up billing · Automatic stop on exit
                </span>
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                {flexPreview.map((opt) => {
                  const isSelected = flexDuration === opt.weeks;
                  return (
                    <button key={opt.weeks} type="button"
                      onClick={() => setFlexDuration(opt.weeks)}
                      className="relative p-2.5 border text-center transition-all"
                      style={{
                        background: isSelected ? "rgba(245,200,66,0.12)" : opt.popular ? "rgba(245,200,66,0.04)" : "rgba(255,255,255,0.02)",
                        borderColor: isSelected ? GOLD : opt.popular ? "rgba(245,200,66,0.3)" : "#2a2a2a",
                        outline: isSelected ? `2px solid ${GOLD}` : "none",
                        outlineOffset: "1px",
                      }}>
                      {opt.popular && !isSelected && (
                        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 font-black text-[7px] uppercase tracking-[0.08em] px-1 py-0.5 whitespace-nowrap"
                          style={{ background: GOLD, color: "#000" }}>Popular</span>
                      )}
                      {isSelected && (
                        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 font-black text-[7px] uppercase tracking-[0.08em] px-1 py-0.5 whitespace-nowrap"
                          style={{ background: GOLD, color: "#000" }}>Selected</span>
                      )}
                      <p className="font-head text-xs font-bold" style={{ color: isSelected ? "#fff" : opt.popular ? "#fff" : "#9CA3AF" }}>{opt.weeks} Wks</p>
                      <p className="text-sm font-black mt-0.5" style={{ color: GOLD }}>${opt.price}/wk</p>
                      {opt.daily && <p className="text-[9px] mt-0.5" style={{ color: "#6B7280" }}>{opt.daily}</p>}
                    </button>
                  );
                })}
              </div>
              {flexPreview.find((o) => o.weeks === flexDuration) && (() => {
                const sel = flexPreview.find((o) => o.weeks === flexDuration);
                return (
                  <div className="flex items-center justify-between px-3 py-2"
                    style={{ background: "rgba(245,200,66,0.06)", border: "1px solid rgba(245,200,66,0.2)" }}>
                    <div>
                      <p className="font-head text-[10px] uppercase tracking-[0.15em]" style={{ color: GOLD }}>
                        {sel.weeks}-week plan · ${sel.price}/wk
                      </p>
                      <p className="text-[10px] mt-0.5" style={{ color: "#6B7280" }}>Billing stops on driver exit</p>
                    </div>
                    <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: GOLD }} />
                  </div>
                );
              })()}
              {!flexSubscribed && (
                <div className="flex items-center gap-2 px-3 py-2.5"
                  style={{ background: "rgba(245,200,66,0.04)", border: "1px solid rgba(245,200,66,0.18)" }}>
                  <CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: GOLD }} />
                  <p className="text-xs font-head" style={{ color: "rgba(245,200,66,0.7)" }}>
                    Fill out your offer below and submit — weekly billing is handled by Hylas.
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {/* Error */}
          <AnimatePresence>
            {error && (
              <motion.p initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="text-red-400 text-sm font-head bg-red-400/10 px-3 py-2">
                {error}
              </motion.p>
            )}
          </AnimatePresence>

          {/* Structured personal offer to driver — all plans */}
          <div className="pt-1 space-y-3">
              <p className={labelCls} style={{ color: "rgba(255,255,255,0.35)" }}>
                Personal Offer to Driver <span className="normal-case font-normal text-white/25">(optional — driver sees this anonymously)</span>
              </p>

              {/* Truck model + years */}
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-3 sm:col-span-1">
                  <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/30 mb-1">Truck Model</p>
                  <select data-testid="offer-truck-model" value={form.offer_truck_model} onChange={set("offer_truck_model")} className={inputCls + " appearance-none"} style={{ cursor: "pointer" }}>
                    <option value="">— Not specified</option>
                    {["Freightliner Cascadia","Kenworth T680","Kenworth W900","Peterbilt 579","Peterbilt 389","Volvo VNL","International LT","Mack Anthem","Western Star 49X"].map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/30 mb-1">Year From</p>
                  <select data-testid="offer-truck-year-from" value={form.offer_truck_year_from} onChange={set("offer_truck_year_from")} className={inputCls + " appearance-none"} style={{ cursor: "pointer" }}>
                    <option value="">—</option>
                    {Array.from({length: 12}, (_, i) => 2015 + i).map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div>
                  <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/30 mb-1">Year To</p>
                  <select data-testid="offer-truck-year-to" value={form.offer_truck_year_to} onChange={set("offer_truck_year_to")} className={inputCls + " appearance-none"} style={{ cursor: "pointer" }}>
                    <option value="">—</option>
                    {Array.from({length: 12}, (_, i) => 2015 + i).map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>

              {/* Home time */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/30 mb-1">Weeks Out</p>
                  <select data-testid="offer-weeks-out" value={form.offer_weeks_out} onChange={set("offer_weeks_out")} className={inputCls + " appearance-none"} style={{ cursor: "pointer" }}>
                    <option value="">— Not specified</option>
                    {["1","2","3","4"].map(w => <option key={w} value={w}>{w} week{w !== "1" ? "s" : ""} out</option>)}
                  </select>
                </div>
                <div>
                  <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/30 mb-1">Days Off at Home</p>
                  <select data-testid="offer-days-off" value={form.offer_days_off} onChange={set("offer_days_off")} className={inputCls + " appearance-none"} style={{ cursor: "pointer" }}>
                    <option value="">— Not specified</option>
                    {["2","3","4","5","6","7"].map(d => <option key={d} value={d}>{d} days off</option>)}
                  </select>
                </div>
              </div>

              {/* Miles */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/30 mb-1">Miles/Week From</p>
                  <select data-testid="offer-miles-from" value={form.offer_miles_from} onChange={set("offer_miles_from")} className={inputCls + " appearance-none"} style={{ cursor: "pointer" }}>
                    <option value="">— Not specified</option>
                    {["1000","1500","2000","2500","3000","3500"].map(m => <option key={m} value={m}>{Number(m).toLocaleString()} mi</option>)}
                  </select>
                </div>
                <div>
                  <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/30 mb-1">Miles/Week To</p>
                  <select data-testid="offer-miles-to" value={form.offer_miles_to} onChange={set("offer_miles_to")} className={inputCls + " appearance-none"} style={{ cursor: "pointer" }}>
                    <option value="">— Not specified</option>
                    {["2000","2500","3000","3500","4000","4500","5000"].map(m => <option key={m} value={m}>{Number(m).toLocaleString()} mi</option>)}
                  </select>
                </div>
              </div>

              {/* Bonuses */}
              <div>
                <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/30 mb-2">Bonuses Offered</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: "offer_signup_bonus", label: "Signup Bonus",        tid: "offer-signup-bonus" },
                    { key: "offer_safety_bonus", label: "Safety Bonus",        tid: "offer-safety-bonus" },
                    { key: "offer_dotw_bonus",   label: "Driver of the Week",  tid: "offer-dotw-bonus" },
                    { key: "offer_dotm_bonus",   label: "Driver of the Month", tid: "offer-dotm-bonus" },
                  ].map(({ key, label, tid }) => (
                    <div key={key}>
                      <p className="font-head text-[8px] uppercase tracking-[0.15em] text-white/25 mb-1">{label}</p>
                      <select
                        data-testid={tid}
                        value={form[key]}
                        onChange={set(key)}
                        className={inputCls + " appearance-none"}
                        style={{ cursor: "pointer" }}
                      >
                        <option value="">— None</option>
                        {Array.from({length: 30}, (_, i) => (i + 1) * 100).map(v => (
                          <option key={v} value={v}>${v.toLocaleString()}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          {/* Submit */}
          <motion.button
            type="submit"
            disabled={loading}
            whileTap={{ scale: 0.97 }}
            className="w-full flex items-center justify-center gap-2 py-3.5 font-head text-sm font-black uppercase tracking-[0.2em] transition-all disabled:opacity-50"
            style={{
              background: form.payment_option === "weekly_flex"
                ? `linear-gradient(90deg, ${GOLD}, #d97706)`
                : `linear-gradient(90deg, ${BID_ACCENT}, #d97706)`,
              color: "white",
              boxShadow: "0 4px 20px rgba(245,158,11,0.25)",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 32px rgba(245,158,11,0.45)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(245,158,11,0.25)"; }}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : form.payment_option === "weekly_flex" ? (
              <><Zap className="w-3.5 h-3.5" /> Submit Flex Bid — Goes Live Instantly</>
            ) : (
              <><Zap className="w-3.5 h-3.5" /> Submit Bid — Goes Live Instantly</>
            )}
          </motion.button>
          <p className="text-white/25 text-xs text-center font-head">
            Bids go live instantly. Hylas Admin picks the winner via Transfer Board.
          </p>
        </form>
      </motion.div>
    </motion.div>
  );
}
